import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { CREATE_PRODUCT_ATTRIBUTE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tAttribute, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import { ERROR } from '../../../../../core/constants/errorCode';

export const AttributeLabel = {
  usage: 'Uso',
  functionality: 'Funcionalidad',
  packaging: 'Embalaje',
  aesthetic: 'Estético',
  completeness: 'Completitud',
  state: 'Estado General',
}

interface Props {
  type: tAttribute;
  cardinality: number;
}

const CreateDialog = (props: Props) => {
  const { type, cardinality } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { errorCode, postProductAttibute, getProductAttributes } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  // Crear schema dinámicamente según el tipo
  const schema = useMemo(() => {
    return yup.object().shape({
      name: yup.string().required('Campo requerido'),
      ...(type !== 'state' && {
        has_description: yup.boolean().required('Campo requerido'),
      }),
    });
  }, [type]);

  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      has_description: false,
    },
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    reset({ name: '', has_description: false });
    closeDialog(CREATE_PRODUCT_ATTRIBUTE_DIALOG);
  }, [closeDialog, reset]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        // Si es estado general, no incluir has_description
        const submitData = type === 'state' ? { name: values.name, cardinality } : { ...values, cardinality };
        postProductAttibute(companyId, submitData, type, () => {
          resetForm();
          getProductAttributes(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, cardinality, postProductAttibute, type, resetForm, getProductAttributes]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_PRODUCT_ATTRIBUTE_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear {AttributeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                  </div>

                  {/* Solo mostrar has_description si NO es estado general */}
                  {type !== 'state' && (
                    <div className="mb-32">
                      <FormControlLabel
                        control={
                          <Controller
                            name="has_description"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={Boolean(field.value ?? false)}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                            )}
                          />
                        }
                        label="Requiere Observación"
                      />
                    </div>
                  )}

                  {errorCode === ERROR.ERR_409 && (
                    <Typography color="error" className="text-md text-center mb-24">
                      Ya existe un estado con ese nombre
                    </Typography>
                  )}

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
